import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2, X } from "lucide-react";
import { OUTLINE_BRAND_BTN_CLASS } from "./constants";
import { formatSubscriptionDateForDisplay } from "./formatters";

export interface SkipNextOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ship date of the next order after the skipped one (post-skip). */
  nextOrderDate?: string | null;
  onConfirm: () => void;
  confirmPending: boolean;
}

export function SkipNextOrderDialog({
  open,
  onOpenChange,
  nextOrderDate,
  onConfirm,
  confirmPending,
}: SkipNextOrderDialogProps) {
  const nextOrderLabel = nextOrderDate
    ? formatSubscriptionDateForDisplay(nextOrderDate)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onOpenChange={onOpenChange}
        overlayClassName="tw:z-70"
        className="tw:z-71 tw:p-4"
      >
        <DialogHeader>
          <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
            <DialogTitle
              className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              Skip upcoming order
            </DialogTitle>
            <DialogClose
              data-slot="dialog-close"
              render={
                <Button
                  variant="ghost"
                  className="tw:p-0 tw:m-0 tw:shrink-0"
                  size="icon-sm"
                  aria-label="Close"
                />
              }
            >
              <X />
            </DialogClose>
          </div>
        </DialogHeader>

        <p
          className="tw:text-sm tw:my-2"
          style={{ color: "var(--so-text, #523D33)" }}
        >
          {nextOrderLabel ? (
            <>
              Your next order will ship on{" "}
              <span className="tw:font-medium">{nextOrderLabel}</span>.
            </>
          ) : (
            "Your next scheduled order will be skipped."
          )}
        </p>

        <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-4 tw:pt-2">
          <Button
            type="button"
            disabled={confirmPending}
            onClick={onConfirm}
            className="tw:w-full tw:rounded-md tw:bg-[#523D33] tw:border-[#523D33] tw:text-[#FAC8CA] hover:tw:bg-[#523D33]/90 tw:font-bold tw:uppercase tw:tracking-wide tw:text-[14px]"
          >
            {confirmPending ? (
              <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
            ) : (
              "Confirm"
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={confirmPending}
            onClick={() => onOpenChange(false)}
            className={`${OUTLINE_BRAND_BTN_CLASS} tw:disabled:opacity-50`}
          >
            Never mind
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
